import React, { useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { InputCustomPaymentConfig } from "lib/types/generated/graphql-types"
import { useLazyQuery, useMutation } from "@apollo/client"
import { hideEditSidebar } from "lib/store/services/editSidebar/slice"
import { useAppDispatch } from "lib/store"
import alertActions from "lib/store/services/Alert/AlertSlice"

import { CREATE_OR_UPDATE_CUSTOM_PAYMENT_CONFIG } from "graphql/mutations/config/Providers/CustomPayment/CreateOrUpdateCustomPaymentConfig"

import EditSidebar from "components/Ui/EditSidebar/EditSidebar"
import EditSidebarHeader from "components/Ui/EditSidebar/EditSidebarHeader"
import PrimaryButton from "components/Ui/Buttons/PrimaryButton"
import Popup from "components/Ui/Popup"
import { CustomPaymentConfigInputs } from "../../../../components/Config/CustomPayment/CustomPaymentConfigInputs"
import { VALIDATE_CUSTOM_PAYMENT } from "../../../../graphql/queries/config/Providers/CustomPayment/ValidateCustomPaymentConfig"
import { handleErrorMessages } from "helpers/errors"

type Props = {
  refetch: () => void
}

interface ExtendedInputCustomPaymentConfig {
  id: string
  customPaymentConfig: InputCustomPaymentConfig
}

export const CreateCustomPaymentConfig = ({ refetch }: Props) => {
  const [validationError, setValidationError] = useState<boolean>(false)
  const dispatch = useAppDispatch()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ExtendedInputCustomPaymentConfig>({ mode: "onBlur" })

  const [addCustomPaymentConfig, { loading: createLoading }] = useMutation(
    CREATE_OR_UPDATE_CUSTOM_PAYMENT_CONFIG,
    {
      onCompleted: () => {
        reset({
          id: "",
          customPaymentConfig: {
            authUrl: "",
            baseUrl: "",
            clientId: "",
            clientSecret: ""
          }
        })
        dispatch(hideEditSidebar())
        dispatch(
          alertActions.actions.createAlert({
            message: "Custom Payment configuration successfully created",
            type: "success"
          })
        )
        refetch()
      },
      onError: () => {
        dispatch(
          alertActions.actions.createAlert({
            type: "error"
          })
        )
      }
    }
  )

  const [validateCustomPayment, { loading }] = useLazyQuery(VALIDATE_CUSTOM_PAYMENT, {
    onError: (error) => handleErrorMessages(error)
  })

  const onSubmit: SubmitHandler<ExtendedInputCustomPaymentConfig> = (data) => {
    const { baseUrl, authUrl, clientId, clientSecret } = data.customPaymentConfig
    validateCustomPayment({
      variables: {
          baseUrl,
          authUrl,
          clientId,
          clientSecret
      }
    }).then((response) => {
      if (response.data.Configuration.validate.customPayment.valid) {
        addCustomPaymentConfig({
          variables: {
            id: data.id,
            customPaymentConfig: {
              baseUrl,
              authUrl,
              clientId,
              clientSecret
            }
          }
        })
      } else {
        setValidationError(true)
      }
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <EditSidebar>
        <EditSidebarHeader title="Create Custom Payment configuration">
          <PrimaryButton type="submit" loading={createLoading || loading}>
            Save
          </PrimaryButton>
        </EditSidebarHeader>
        {validationError ? (
          <Popup
            title="Validation error"
            subtitle="Couldn't validate provided credentials with Custom Payment"
            buttonText="Yes, save"
            cancelButtonText={"Close"}
            hideOkButton={true}
            handleCloseClick={() => setValidationError(false)}
          />
        ) : (
          <></>
        )}
        <CustomPaymentConfigInputs control={control} errors={errors} />
      </EditSidebar>
    </form>
  )
}
